// Acompanhamento mensal do agrupamento de faturamento (#5783).
//
// A classificação UC × mês NÃO é reimplementada aqui: vem de `conferenciaFaturamento.ts`, que já
// decide faturado / não faturado / não verificável, o detalhe e o prazo, e é testada. Este arquivo
// só monta a base a partir das UCs do agrupamento (em vez da planilha de rateio) e responde as
// duas perguntas do fechamento: o que falta resolver e quanto vai para o boleto.
//
// Regras confirmadas com o dono do produto em 15/09/2026:
//   - Total zero é fechamento válido (o contrato exige emitir mesmo sem injeção).
//   - UC sem fatura no mês NÃO conta como resolvida: obriga decidir entre esperar ou pular.
// E em 21/09/2026 (#5785): a UC do agrupamento é CALCULADA e não ganha boleto próprio — o boleto é
// um só, do agrupamento. 'faturada' passou a ser a fatura calculada; a UC que já tem boleto próprio
// vira 'cobrada_a_parte' (resolvida, mas fora do total, senão cobraria duas vezes). Mesma regra de
// situacao_ucs_fechamento no banco (migration 20260921150000).

import {
  montarConferencia,
  type FaturaAnteriorConferencia,
  type FaturaConferencia,
  type ItemRateioConferencia,
  type LinhaConferencia,
} from './conferenciaFaturamento.ts';

export interface UcDoAgrupamento {
  id: number;
  numero_uc: string;
  outro_numero_uc?: string | null;
  nome_cliente?: string | null;
  cpf_cnpj_cliente?: string | null;
}

/** Estado da UC dentro do fechamento — é o que a tela mostra e o que a trava da #5784 vai ler. */
export type EstadoNoFechamento = 'faturada' | 'cobrada_a_parte' | 'pulada' | 'dispensada' | 'aguardando_fatura' | 'pendente';

export const ROTULO_ESTADO: Record<EstadoNoFechamento, string> = {
  faturada: 'Calculada',
  cobrada_a_parte: 'Boleto próprio',
  pulada: 'Cálculo pulado',
  dispensada: 'Dispensada do mês',
  aguardando_fatura: 'Fatura não chegou',
  pendente: 'Pendente',
};

export interface LinhaFechamento {
  unidadeConsumidoraId: number;
  numeroUc: string;
  cliente: string;
  estado: EstadoNoFechamento;
  /** true quando o estado já permite fechar (calculada, cobrada à parte, pulada ou dispensada). */
  resolvida: boolean;
  /** Valor que entra no boleto agrupado. 0 quando não há o que cobrar. */
  valorVigor: number;
  /** Linha da classificação, para a tela mostrar detalhe, prazo e motivo sem recalcular nada. */
  conferencia: LinhaConferencia;
}

export interface ResumoFechamento {
  ucs: number;
  resolvidas: number;
  pendentes: number;
  faturadas: number;
  cobradasAParte: number;
  puladas: number;
  dispensadas: number;
  aguardandoFatura: number;
  totalVigor: number;
  /** Pode emitir: todas resolvidas e pelo menos uma UC no grupo. Total zero não impede. */
  podeFechar: boolean;
  /** Números das UCs que faltam resolver, para a tela dizer o nome em vez de só desabilitar. */
  ucsPendentes: string[];
}

const norm = (v: string | number | null | undefined) => String(v ?? '').replace(/\D/g, '').replace(/^0+/, '');

/** Status da fatura calculada e ainda sem boleto — é ela que o boleto agrupado cobra. */
export const STATUS_CALCULADA = 'Com boleto calculado';

/** Fatura calculada, sem boleto próprio e sem cálculo pulado. */
export function faturaCalculada(f: { status_fatura?: string | null; emitido_em?: string | null; pular_calculo_em?: string | null; valor_boleto_vigor?: unknown } | null | undefined): boolean {
  if (!f || f.emitido_em || f.pular_calculo_em) return false;
  return f.status_fatura === STATUS_CALCULADA && f.valor_boleto_vigor != null && f.valor_boleto_vigor !== '';
}

/** Valor que a UC leva ao boleto agrupado, já tratando string vinda do PostgREST e valor nulo. */
export function valorVigorDaFatura(f: { valor_boleto_vigor?: unknown } | null | undefined): number {
  const v = f?.valor_boleto_vigor;
  const n = typeof v === 'string' ? Number(v) : v;
  return typeof n === 'number' && Number.isFinite(n) ? n : 0;
}

export interface EntradaFechamento {
  ucs: UcDoAgrupamento[];
  faturas: (FaturaConferencia & { valor_boleto_vigor?: unknown; status_fatura?: string | null })[];
  faturasMesAnterior?: FaturaAnteriorConferencia[];
  /**
   * UCs dispensadas do mês (#5784): sem fatura e com motivo registrado no fechamento. Contam como
   * resolvidas — sem isso o mês nunca fecharia quando a fatura não chega (43 das 160 UCs da
   * Prefeitura em AGO/2026). Dispensa só vale para UC sem fatura; se a fatura chegar depois, o
   * estado real volta a mandar.
   */
  dispensadas?: Iterable<number>;
  hojeIso: string;
}

/**
 * Monta uma linha por UC do agrupamento, na ordem recebida. UC sem fatura no mês continua na
 * lista: some da tela é exatamente o que faria a UC ficar fora da cobrança sem ninguém ver.
 */
export function montarFechamento({ ucs: ucsEntrada, faturas, faturasMesAnterior = [], dispensadas = [], hojeIso }: EntradaFechamento): LinhaFechamento[] {
  const dispensadasSet = new Set(dispensadas);
  // O boleto agrupado é uma fatura AVULSO: ela não é fatura de UC e não pode casar com nenhuma.
  faturas = faturas.filter(f => String((f as { classificacao?: unknown }).classificacao ?? '').toUpperCase() !== 'AVULSO');
  // Deduplica por id e por número: a mesma UC repetida na lista casaria com a mesma fatura duas
  // vezes e dobraria o valor do boleto agrupado. Vale a primeira ocorrência.
  const vistos = new Set<string>();
  const ucs = ucsEntrada.filter(u => {
    const chaves = [`id:${u.id}`, `uc:${norm(u.numero_uc)}`].filter(k => !k.endsWith(':'));
    if (chaves.some(k => vistos.has(k))) return false;
    chaves.forEach(k => vistos.add(k));
    return true;
  });

  const itens: ItemRateioConferencia[] = ucs.map(u => ({
    uc: u.numero_uc,
    outro_numero_uc: u.outro_numero_uc ?? null,
    status: null,
    nome: u.nome_cliente ?? null,
    cpf_cnpj: u.cpf_cnpj_cliente ?? null,
    uc_geradora: null,
  }));

  const { linhas } = montarConferencia({ itensRateio: itens, faturas, faturasMesAnterior, hojeIso });

  const porChave = new Map<string, LinhaConferencia>();
  for (const l of linhas) {
    for (const k of [l.chave, norm(l.uc), norm(l.outroNumeroUc)]) if (k) porChave.set(k, l);
  }
  const faturaPorId = new Map(faturas.map(f => [f.id, f]));

  return ucs.map(u => {
    const c = porChave.get(norm(u.numero_uc)) ?? porChave.get(norm(u.outro_numero_uc)) ?? null;
    const semFatura = !c || c.detalhe === 'fatura_nao_recebida';
    const fatura = c?.faturaId != null ? faturaPorId.get(c.faturaId) : undefined;
    const estado: EstadoNoFechamento = !c
      ? (dispensadasSet.has(u.id) ? 'dispensada' : 'aguardando_fatura')
      : c.situacao === 'faturado'
        ? 'cobrada_a_parte'
        : faturaCalculada(fatura)
          ? 'faturada'
          : c.puladoEm
          ? 'pulada'
          // Dispensa só vale enquanto não há fatura: se ela chegar, o estado real volta a mandar.
          : semFatura && dispensadasSet.has(u.id)
            ? 'dispensada'
            : semFatura
              ? 'aguardando_fatura'
              : 'pendente';

    return {
      unidadeConsumidoraId: u.id,
      numeroUc: u.numero_uc,
      cliente: u.nome_cliente ?? c?.cliente ?? '',
      estado,
      resolvida: estado !== 'aguardando_fatura' && estado !== 'pendente',
      // Só a calculada leva valor: a pulada não tem cálculo e a cobrada à parte já tem boleto.
      valorVigor: estado === 'faturada' ? valorVigorDaFatura(fatura) : 0,
      conferencia: c ?? linhaVazia(u),
    };
  });
}

/** Linha mínima para UC que não casou com nenhuma fatura — a tela lê os mesmos campos. */
function linhaVazia(u: UcDoAgrupamento): LinhaConferencia {
  return {
    chave: norm(u.numero_uc), uc: u.numero_uc, outroNumeroUc: u.outro_numero_uc ?? null,
    cliente: u.nome_cliente ?? '', cpfCnpj: u.cpf_cnpj_cliente ?? null, statusRateio: '', usinas: [],
    marcacoesRateio: [], situacao: 'nao_verificavel', detalhe: 'fatura_nao_recebida', faturaId: null,
    quantidadeFaturas: 0, dataVencimento: null, dataApresentacao: null, leituraAtual: null, chegadaEm: null,
    limiteEmissao: null, estadoPrazo: 'sem_prazo', diasPrazo: null, emitidoEm: null, emitidoPor: null,
    reemitido: false, atrasoJustificado: null, motivoAtraso: null, puladoEm: null, puladoPor: null,
    motivoCodigo: null, motivoRotulo: null, motivoTexto: null, motivoLegado: false,
    chegadaPrevista: null, chegadaAtrasada: false,
  };
}

export function resumirFechamento(linhas: LinhaFechamento[]): ResumoFechamento {
  const conta = (e: EstadoNoFechamento) => linhas.filter(l => l.estado === e).length;
  const resolvidas = linhas.filter(l => l.resolvida).length;
  const pendentes = linhas.filter(l => !l.resolvida);
  return {
    ucs: linhas.length,
    resolvidas,
    pendentes: pendentes.length,
    faturadas: conta('faturada'),
    cobradasAParte: conta('cobrada_a_parte'),
    puladas: conta('pulada'),
    dispensadas: conta('dispensada'),
    aguardandoFatura: conta('aguardando_fatura'),
    // Soma em centavos e volta: evita o 0.1 + 0.2 do ponto flutuante em 159 parcelas.
    totalVigor: Math.round(linhas.reduce((s, l) => s + Math.round(l.valorVigor * 100), 0)) / 100,
    podeFechar: linhas.length > 0 && pendentes.length === 0,
    ucsPendentes: pendentes.map(l => l.numeroUc),
  };
}

/** Frase da trava: diz o que falta em vez de só desabilitar o botão. */
export function motivoNaoPodeFechar(r: ResumoFechamento): string | null {
  if (r.ucs === 0) return 'Este agrupamento não tem nenhuma UC. Atribua as UCs antes de fechar o mês.';
  if (r.pendentes === 0) return null;
  const lista = r.ucsPendentes.slice(0, 5).join(', ');
  const resto = r.ucsPendentes.length > 5 ? ` e mais ${r.ucsPendentes.length - 5}` : '';
  return `Faltam ${r.pendentes} UC(s): ${lista}${resto}. Calcule, pule o cálculo ou dispense cada uma.`;
}

// ─── Competência ────────────────────────────────────────────────────────────────────────────
// No banco a competência é date (dia 1); na tela é "AGO/2026", como no resto do sistema.

const MESES = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];

/** "AGO/2026" → "2026-08-01". Devolve null para texto que não é competência. */
export function competenciaParaData(mes: string | null | undefined): string | null {
  const m = String(mes ?? '').trim().toUpperCase();
  const casa = /^([A-Z]{3})\/(\d{4})$/.exec(m);
  if (!casa) return null;
  const i = MESES.indexOf(casa[1]);
  if (i < 0) return null;
  return `${casa[2]}-${String(i + 1).padStart(2, '0')}-01`;
}

/** "2026-08-01" (ou timestamp do mesmo dia) → "AGO/2026". */
export function dataParaCompetencia(data: string | null | undefined): string | null {
  const casa = /^(\d{4})-(\d{2})/.exec(String(data ?? '').trim());
  if (!casa) return null;
  const i = Number(casa[2]) - 1;
  if (i < 0 || i > 11) return null;
  return `${MESES[i]}/${casa[1]}`;
}
